// src/components/product/SubscriptionProducts.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import { Oval } from "react-loader-spinner";
import { useAffiliate } from "../../hooks/useAffiliate";
import { selectUploadedImageIds } from "../../store/customizerSlice";
import { useGetProductsQuery } from "../../store/apiSlice";
import ProductBox from "./ProductBox";
import ProductDetailsModal from "./ProductDetailsModal";
import { addToCart, openCart } from "../../store/cartSlice";
import useProductModal from "../../hooks/useProductModal";
import { getImageFromDB, saveImageToDB } from "../../utils/indexedDB";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { fbqTrack } from "../../utils/eventsTracking/fbq";
import { gtagEvent } from "../../utils/eventsTracking/gtag";
import { trackCustomEvent } from "../../utils/eventsTracking/trackCustomEvent";

const productsGrouped = (arr, size) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) result.push(arr.slice(i, i + size));
  return result;
};

const SubscriptionProducts = ({ subscriptionQuantity, setCurrentStep }) => {
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const uploadedImageIds = useSelector(selectUploadedImageIds);
  const { affiliate, isLoading: isLoadingAffiliate } = useAffiliate();
  const { data: products, isLoading: isLoadingProducts } = useGetProductsQuery();
  const canvasRef = useRef(null);
  const [finalImages, setFinalImages] = useState({});
  const [allImages, setAllImages] = useState([]);
  const [selectedPetType, setSelectedPetType] = useState(null);
  const [resultImageId, setResultImageId] = useState(null);
  const [subscriptions, setSubscriptions] = useState({});
  const {
    isOpen: isProductModalActive,
    modalData: productDetailsModalFor,
    openModal,
    closeModal
  } = useProductModal();

  useEffect(() => {
    const handler = () => {
      const petType = localStorage.getItem("petType");
      if (petType) setSelectedPetType(petType);
    };
    handler();
    window.addEventListener("petTypeReady", handler);
    return () => window.removeEventListener("petTypeReady", handler);
  }, []);

  useEffect(() => {
    const handler = () => {
      const resultId = localStorage.getItem("resultImageId");
      if (resultId) setResultImageId(resultId);
    };
    handler();
    window.addEventListener("resultImageReady", handler);
    return () => window.removeEventListener("resultImageReady", handler);
  }, []);

  // For affiliate-specific product filtering
  const [affiliateProductIds, setAffiliateProductIds] = useState(null);
  
  // Debug the affiliate object
  useEffect(() => {
    console.log('SubscriptionProducts - Affiliate object:', affiliate);
    if (affiliate) {
      console.log('Affiliate ID:', affiliate.id);
      console.log('Affiliate Code:', affiliate.code);
      console.log('All affiliate properties:', Object.keys(affiliate));
    }
  }, [affiliate]);
  
  // Fetch affiliate product mappings if affiliate ID exists
  useEffect(() => {
    console.log('Checking affiliate for product mappings:', { 
      affiliateExists: !!affiliate, 
      affiliateId: affiliate?.id 
    });
    
    if (!affiliate?.id) {
      console.log('No affiliate ID found, showing all products');
      setAffiliateProductIds(null);
      return;
    }

    const fetchAffiliateProducts = async () => {
      try {
        console.log('Fetching affiliate products for ID:', affiliate.id);
        // Try production URL since localhost seems to be refused
        const response = await fetch(`https://wmmiktznldojshfxhgnh.supabase.co/functions/v1/affiliate-product-mappings?affiliate_id=${affiliate.id}`);
        console.log('Response status:', response.status);
        
        if (response.ok) {
          const mappings = await response.json();
          console.log('Affiliate product mappings:', mappings);
          const productIds = mappings.map(m => m.product_id);
          console.log('Filtered product IDs:', productIds);
          setAffiliateProductIds(productIds);
        } else {
          const errorText = await response.text();
          console.error('Failed to fetch affiliate products, status:', response.status, 'response:', errorText);
          setAffiliateProductIds([]);
        }
      } catch (error) {
        console.error('Error fetching affiliate products:', error);
        setAffiliateProductIds([]);
      }
    };

    fetchAffiliateProducts();
  }, [affiliate?.id]);

  // Filter products based on affiliate mapping and pet type
  const filteredProducts = useMemo(() => {
    console.log('Filtering products:', {
      totalProducts: products?.length,
      affiliateId: affiliate?.id,
      affiliateProductIds,
      selectedPetType
    });
    
    if (!products) return [];
    
    let filteredByAffiliate = products;
    
    // If affiliate exists and we have product mappings, filter by them
    if (affiliate?.id && affiliateProductIds !== null) {
      filteredByAffiliate = products.filter(product => 
        affiliateProductIds.includes(product.ID)
      );
      console.log('Products after affiliate filtering:', filteredByAffiliate.length);
    }
    
    // Then filter by pet type if selected
    if (!selectedPetType) return filteredByAffiliate;
    
    const finalFiltered = filteredByAffiliate.filter(
      (product) =>
        product.petType?.toLowerCase() === selectedPetType.toLowerCase()
    );
    console.log('Products after pet type filtering:', finalFiltered.length);
    
    return finalFiltered;
  }, [products, selectedPetType, affiliate?.id, affiliateProductIds]);

  useEffect(() => {
    if (!products || !filteredProducts || isLoadingAffiliate || isLoadingProducts || !resultImageId) return;

    const applyPrintToProducts = async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      let updatedImages = {};

      for (const product of filteredProducts) {
        const onProductImage = await getImageFromDB("onProductImages", null, { productId: product.id, resultImageId });
        if (onProductImage) {
          updatedImages[product.id] = URL.createObjectURL(onProductImage);
          continue;
        }

        const mockupImageSrc = product.image;
        const mockupImage = new Image();
        mockupImage.src = mockupImageSrc;
        mockupImage.crossOrigin = "anonymous";
        await new Promise((resolve) => {
          mockupImage.onload = async () => {
            const imageBlob = await getImageFromDB("resultImages", resultImageId);
            if (!imageBlob) return resolve();
            const customizedImage = new Image();
            customizedImage.src = URL.createObjectURL(imageBlob);
            customizedImage.crossOrigin = "anonymous";

            customizedImage.onload = () => {
              const canvasWidth = mockupImage.width;
              const canvasHeight = mockupImage.height;
              canvas.width = canvasWidth;
              canvas.height = canvasHeight;

              ctx.clearRect(0, 0, canvasWidth, canvasHeight);
              ctx.globalCompositeOperation = "source-over";
              ctx.drawImage(mockupImage, 0, 0, canvasWidth, canvasHeight);

              const overlayWidth = canvasWidth * 0.36;
              const overlayHeight = (overlayWidth * customizedImage.height) / customizedImage.width;
              const overlayX = (canvasWidth - overlayWidth) / 2;
              const overlayY = (canvasHeight - overlayHeight) / 1.65;
              ctx.drawImage(customizedImage, overlayX, overlayY, overlayWidth, overlayHeight);
              ctx.globalCompositeOperation = "source-over";

              canvas.toBlob(async (finalBlob) => {
                if (!finalBlob) return resolve();
                await saveImageToDB("onProductImages", `${product.id}_${resultImageId}`, finalBlob, product.id, resultImageId);
                updatedImages[product.id] = URL.createObjectURL(finalBlob);
                resolve();
              }, "image/png");
            };
          };
        });
      }

      setFinalImages(updatedImages);
    };

    applyPrintToProducts();
  }, [products, filteredProducts, isLoadingProducts, isLoadingAffiliate, resultImageId]);

  const handleAddToCart = async (product, isSubscribed, quantity) => {
    closeModal();

    dispatch(addToCart({
      ...product,
      quantity: subscriptionQuantity || 1,
      subscription: true,
      imageId: resultImageId,
      uploadedImageIds
    }));
    setCurrentStep(1);
    navigate('/checkout');

    // Events for marketing
    trackCustomEvent('added_to_cart', {
      sku: product.sku,
      price: product.price,
      title: product.title,
      flavor: product.flavor,
      quantity: subscriptionQuantity || 1,
      subscription: true,
      context: 'bundle product'
    }, affiliate?.code || null);
    fbqTrack('AddToCart', {
      contents: [
        {
          id: product.sku,
          quantity: subscriptionQuantity || 1,
          item_price: product.price,
          title: product.title,
          flavor: product.flavor,
          subscription: true
        }
      ],
      content_ids: [product.sku],
      content_type: 'product',
      content_name: product.title,
      content_category: 'bundle product',
      value: product.price * subscriptionQuantity || 1,
      currency: 'USD',
      context: 'bundle product'
    });
    gtagEvent('add_to_cart', {
      currency: 'USD',
      value: product.price * subscriptionQuantity || 1,
      items: [
        {
          item_id: product.sku,
          item_name: product.title,
          item_category: 'bundle product',
          quantity: subscriptionQuantity || 1,
          price: product.price,
          flavor: product.flavor,
          subscription: true
        }
      ]
    });
  };

  if (isLoadingProducts || isLoadingAffiliate) {
    return (
      <div className="flex justify-center items-center w-full h-screen min-h-screen">
        <Oval visible={true} height={50} width={50} color="#a99cdf" secondaryColor="#e2e6ff" strokeWidth="3" />
      </div>
    );
  }

  return (
    <div className="product-boxes home-slider-container">
      {filteredProducts?.length > 0 ?
        <div className="product-boxes home-slider-container">
          <Swiper
            modules={[Pagination]}
            pagination={{ el: ".slider-pagination", clickable: true }}
            slidesOffsetAfter={0}
            slidesPerGroup={1}
            breakpoints={{
              300: { slidesPerView: 1 },
              700: { slidesPerView: 2 },
              1000: { slidesPerView: 3 },
            }}
          >
            {productsGrouped(filteredProducts, 2).map((productChunk, index) => (
              <SwiperSlide key={`slide-${index}`}>
                {productChunk.map((product) => (
                  <ProductBox
                    key={product.id}
                    product={product}
                    finalImages={finalImages}
                    openModal={openModal}
                    handleAddToCart={handleAddToCart}
                    subscriptions={subscriptions}
                  />
                ))}
              </SwiperSlide>
            ))}
            <div className="slider-pagination" />
          </Swiper>
        </div>
        :
        <p className="text-gray-500 text-center col-span-full color-swap">
          {affiliate ? 
            `No products available for ${affiliate.name}.` : 
            "No products found."
          }
        </p>
      }
      <ProductDetailsModal
        isOpen={isProductModalActive}
        onClose={closeModal}
        product={productDetailsModalFor}
        handleAddToCart={handleAddToCart}
        allImages={allImages}
        setAllImages={setAllImages}
        image={
          productDetailsModalFor
            ? finalImages[productDetailsModalFor.id] || productDetailsModalFor.image
            : ""
        }
        withSubscriptionToggle={false}
        subscriptionQuantity={subscriptionQuantity}
      />
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default SubscriptionProducts;
